import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { MediaSearchGallery } from "../components/search/MediaSearchGallery";
import { useCollections } from "../hooks/useCollections";
import { Loading } from "../components/Spinner";
import { NotFoundPage } from "./NotFoundPage";

/**
 * One collection's own section, at `/collections/:id`.
 *
 * Unlike the Photos and Videos galleries this is not locked to a content type:
 * a section is the whole shelf, so the scuba page shows dive photos, dive
 * clips, and dive write-ups together. Locking the query to the collection also
 * means the tag and year facets are scoped to it — the dive sites and critters
 * get a filter list of their own instead of competing with everything else.
 */
export function CollectionPage() {
  const { id = "" } = useParams();
  const { collections, loading } = useCollections();

  const collection = collections.find((c) => c.id === id);

  // Stable identity for the gallery's collection lock.
  const locked = useMemo(() => [id], [id]);

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <Loading label="Loading collection…" />
        </div>
      </div>
    );
  }

  if (!collection) {
    return <NotFoundPage />;
  }

  return (
    <div className="page">
      <div className="container">
        <header className="page__head">
          <p className="eyebrow">
            <Link to="/collections">Collections</Link> · {collection.label}
          </p>
          <h1 className="page__title">{collection.title}</h1>
          {collection.blurb && <p className="page__intro">{collection.blurb}</p>}
        </header>

        <MediaSearchGallery
          collections={locked}
          noun="item"
          placeholder={`Search ${collection.label.toLowerCase()}…`}
          emptyLabel={`Nothing in ${collection.label} matches your search.`}
        />
      </div>
    </div>
  );
}
